const {
	findArguments,
	findOptionalArgument,
	makeFormattedText,
	makeMathCommandNode,
	makeNonmathCommandNode,
	parseAndIgnoreSpace,
} = require("../util/ParserHelpers");
const {
	calculateAdditionalBottomSpace,
	getDigitWidth,
	getLineWidth,
} = require("../util/RenderHelpers");
const Font = require("../../util/Font");

// PARSERS

const makeBasicParser = function(command) {
	return function(ps) {
		return makeMathCommandNode(command, findArguments(ps, 1));
	};
};

const blockBoxedParser = function(ps) {
	const nodes = makeFormattedText("blockBoxed", findArguments(ps, 1)[0]);
	parseAndIgnoreSpace(ps, {newlineLimit: 1});
	return nodes;
};

const boxedNonmathParser = function(ps) {
	const numSpaces = findOptionalArgument(ps);
	const nodes = makeNonmathCommandNode("boxed", findArguments(ps, 1));
	if (nodes) {
		nodes[0].numSpaces = numSpaces ? parseInt(numSpaces.text) : false;
	}
	return nodes;
};

const boxedMathParser = function(ps) {
	const numSpaces = findOptionalArgument(ps);
	const args = findArguments(ps, 1);
	return [
		{
			type: "boxed",
			numSpaces: numSpaces ? parseInt(numSpaces.text) : false,
			children: [
				{
					type: "math",
					children: ps.parseMath(args[0]),
				},
			],
		},
	];
};

// NODES

const bigboxedNode = {
	draw(rc, x, y, node, measure) {
		const context = rc.context;
		const {fontInfo, width, top, bottom} = measure;
		const lineWidth = getLineWidth(rc, fontInfo);
		const digitWidth = getDigitWidth(rc, fontInfo);

		context.save();
		context.font = Font.fromInfo(fontInfo);
		context.lineWidth = lineWidth;
		context.beginPath();
		context.rect(x, y - (3 * lineWidth) / 4 - top, width, top + bottom);
		context.stroke();
		context.restore();
		rc.drawNode(rc, x + digitWidth / 2, y, node.children[0]);
	},
	measure(rc, fontInfo, props, node) {
		const inBoxMeasured = rc.measureNode(rc, fontInfo, props, node.children[0]);
		const lineWidth = getLineWidth(rc, fontInfo);
		const digitWidth = getDigitWidth(rc, fontInfo);
		const yBuffer = 2 * lineWidth;
		const extraBottomSpace = calculateAdditionalBottomSpace(node, lineWidth);
		return {
			width: inBoxMeasured.width + digitWidth,
			top: inBoxMeasured.top + yBuffer,
			bottom: inBoxMeasured.bottom + yBuffer + extraBottomSpace,
			fontInfo,
		};
	},
	readAloud: {children: true},
};

const blankboxedNode = {
	//similar to bigboxed, but box is not drawn
	draw(rc, x, y, node, measure) {
		const {fontInfo} = measure;
		const digitWidth = getDigitWidth(rc, fontInfo);
		rc.drawNode(rc, x + digitWidth / 2, y, node.children[0]);
	},
	measure(rc, fontInfo, props, node) {
		const inBoxMeasured = rc.measureNode(rc, fontInfo, props, node.children[0]);
		const lineWidth = getLineWidth(rc, fontInfo);
		const digitWidth = getDigitWidth(rc, fontInfo);
		const yBuffer = 2 * lineWidth;
		return {
			width: inBoxMeasured.width + digitWidth,
			top: inBoxMeasured.top + yBuffer,
			bottom: inBoxMeasured.bottom + yBuffer,
			fontInfo,
		};
	},
	readAloud: {children: true},
};

const blockBoxedNode = {
	isBlock: true,
	draw(rc, x, y, node, measure) {
		const context = rc.context;
		const {fontInfo, width, height} = measure;
		const lineWidth = getLineWidth(rc, fontInfo);

		context.save();
		context.font = Font.fromInfo(fontInfo);
		context.lineWidth = lineWidth;
		context.beginPath();
		context.rect(x, y, width, height);
		context.stroke();
		context.restore();
		rc.drawNode(rc, x + 2 * lineWidth, y + 2 * lineWidth, node.children[0]);
	},
	measure(rc, fontInfo, props, node) {
		const inBoxMeasured = rc.measureNode(rc, fontInfo, props, node.children[0]);

		if (!rc.isBlockNode(node.children[0]) && !rc.isErrored(node)) {
			rc.pushError(
				"blockBoxed contains " +
					node.children[0].type +
					" node which is not a block node."
			);
			rc.setErrored(node);
		}

		const lineWidth = getLineWidth(rc, fontInfo);
		return {
			width: inBoxMeasured.width + 4 * lineWidth,
			height: inBoxMeasured.height + 4 * lineWidth,
			fontInfo,
		};
	},
	readAloud: {children: true},
};

const boxedNode = {
	draw(rc, x, y, node, measure) {
		const context = rc.context;
		const {fontInfo, width, top, bottom} = measure;
		const lineWidth = getLineWidth(rc, fontInfo);

		context.save();
		context.font = Font.fromInfo(fontInfo);
		context.lineWidth = lineWidth;
		context.beginPath();
		context.rect(x, y - top + lineWidth / 2, width, top + bottom - lineWidth);
		context.stroke();
		context.restore();
		if (node.children.length) {
			rc.drawNode(rc, x + 2 * lineWidth, y, node.children[0]);
		}
	},
	measure(rc, fontInfo, props, node) {
		const lineWidth = getLineWidth(rc, fontInfo);
		let inBoxMeasured;
		let extraBottomSpace = 0;
		if (node.children[0].children.length) {
			inBoxMeasured = rc.measureNode(rc, fontInfo, props, node.children[0]);
			extraBottomSpace = calculateAdditionalBottomSpace(node, lineWidth);
		} else {
			const numSpaces = !node.numSpaces ? 1 : node.numSpaces;
			const context = rc.context;
			let boxText = "";
			for (let c = 0; c < numSpaces; c++) {
				boxText += "0";
			}
			context.save();
			context.font = Font.fromInfo(fontInfo);
			inBoxMeasured = {
				width: context.measureText(boxText).width,
				top: fontInfo.size / 2,
				bottom: fontInfo.size / 2,
			};
			context.restore();
		}
		return {
			width: inBoxMeasured.width + 4 * lineWidth,
			top: inBoxMeasured.top + (lineWidth * 3) / 2 + extraBottomSpace / 2,
			bottom: inBoxMeasured.bottom + (lineWidth * 3) / 2 + extraBottomSpace,
			fontInfo,
		};
	},
	readAloud: {children: true},
};

module.exports = {
	parsers: {
		nonmathCommands: {
			blockboxed: blockBoxedParser,
			blockBoxed: blockBoxedParser,
			boxed: boxedNonmathParser,
		},
		mathCommands: {
			bigboxed: makeBasicParser("bigboxed"),
			blankboxed: makeBasicParser("blankboxed"),
			boxed: boxedMathParser,
		},
	},
	nodes: {
		bigboxed: bigboxedNode,
		blankboxed: blankboxedNode,
		blockBoxed: blockBoxedNode,
		boxed: boxedNode,
	},
};
